
import { computed, defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import Sidebar from 'primevue/sidebar';
import Button from 'primevue/button';
import type { HistoryEntry, FlowActionType, HistoryEntryMeta } from '@/composables/flows/useFlowHistory';
import type { FlowStructureDto } from '@/types/flow-structure';
import { formatHistoryMetaLabel } from '@/composables/flows/useFlowEditor';

export default defineComponent({
  name: 'FlowHistoryPanel',
  components: {
    Sidebar,
    Button,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    undoStack: {
      type: Array as PropType<HistoryEntry[]>,
      default: () => [],
    },
    redoStack: {
      type: Array as PropType<HistoryEntry[]>,
      default: () => [],
    },
  },
  emits: ['close', 'restore'],
  setup(props, { emit }) {
    const { t } = useI18n();

    // Reverse undo stack to show most recent first, and keep original index
    const undoEntries = computed(() => {
      return props.undoStack
        .map((entry, index) => ({ ...entry, originalIndex: index }))
        .reverse();
    });

    // Reverse redo stack to show most recent first
    const redoEntries = computed(() => {
      return [...props.redoStack].reverse();
    });

    const formatActionType = (actionType: FlowActionType): string => {
      return t(`flows.history.actions.${actionType}`);
    };

    // Format description based on actionType and meta params
    const formatMetaLabel = (meta: HistoryEntryMeta | undefined, actionType: FlowActionType, snapshot?: FlowStructureDto): string | null => {
      return formatHistoryMetaLabel(actionType, meta, snapshot);
    };

    const formatTime = (timestamp: number): string => {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);

      const diffDays = Math.floor(diffMs / 86400000);
      const diffMonths = Math.floor(diffMs / 2592000000);
      const diffYears = Math.floor(diffMs / 31536000000);

      if (diffMins < 1) {
        return t('flows.historyPanel.justNow');
      } else if (diffMins < 60) {
        return t('flows.historyPanel.minutesAgo', { count: diffMins });
      } else if (diffHours < 24) {
        return t('flows.historyPanel.hoursAgo', { count: diffHours });
      } else if (diffDays < 30) {
        return t('flows.historyPanel.daysAgo', { count: diffDays });
      } else if (diffMonths < 12) {
        return t('flows.historyPanel.monthsAgo', { count: diffMonths });
      } else {
        return t('flows.historyPanel.yearsAgo', { count: diffYears });
      }
    };

    const restoreEntry = (originalIndex: number) => {
      emit('restore', originalIndex);
    };

    return {
      t,
      undoEntries,
      redoEntries,
      formatActionType,
      formatMetaLabel,
      formatTime,
      restoreEntry,
    };
  },
});
